import {
    NOTICE_GET_ALL,
    NOTICE_SET_FILTERS,
    NOTICE_CLEAR_FILTERS,
    NOTICE_SET_STATE,
    NOTICE_PAGE_RESET,
    NOTICE_SET_LOADING,
    NOTICE_SET_ERROR,
    NOTICE_CREATE,
    NOTICE_CREATE_SUCCESS,
    NOTICE_CREATE_FAILURE
} from "./NoticeActionType";

const apiConfig = window as any;

const BASE_API_URL = apiConfig.BASE_API_URL?.charAt(apiConfig.BASE_API_URL.length - 1) === "/"
    ? apiConfig.BASE_API_URL.substring(0, apiConfig.BASE_API_URL.length - 1)
    : apiConfig.BASE_API_URL;

console.log("Notice API URL:", BASE_API_URL);

const BasicAuthentication = apiConfig.BasicAuthentication;

const postAuthorizedHeaders = {
    "Content-Type": "application/json",
    "Accept": "application/json",
    ...(BasicAuthentication ? { "Authorization": "Basic " + btoa(BasicAuthentication) } : {})
};

// Notice filter request interface
export interface NoticeFilterRequest {
    includeInactive?: boolean;
    category?: string;
    priority?: string;
    year?: string;
}

// Create notice request interface - EXACT payload structure from page
export interface CreateNoticeRequest {
    action: string;        // 'I' for Insert, 'U' for Update, 'D' for Delete
    id: number;            // 0 for new notice
    category: string;
    priority: string;
    year: string;
    title: string;
    content: string;
    author: string;
    commentCount: number;
    readCount: number;
    totalCount: number;
    isActive: boolean;
}

// Response interface - Matches your NoticeResponse
export interface NoticeResponse {
    Id: number;
    StatusMessage: string;
    StatusCode: number;
    ResponseDate: string;
}

// Get all notices with filters
export const GetAllNotices = (filterRequest: NoticeFilterRequest = { includeInactive: false }) => async (dispatch: any) => {
    dispatch({ type: NOTICE_SET_LOADING, payload: true });
    
    try {
        console.log("Fetching notices with filters:", filterRequest);
        
        const response = await fetch(
            `${BASE_API_URL}/NoticeDetails`,
            {
                method: "POST",
                headers: postAuthorizedHeaders,
                body: JSON.stringify(filterRequest)
            }
        );
        const data = await response.json();
        
        console.log("Notices response:", data);
        
        dispatch({ type: NOTICE_SET_LOADING, payload: false });
        
        return dispatch({
            type: NOTICE_GET_ALL,
            payload: data
        });
    } catch (err) {
        console.error("Error in NoticeAction.ts > GetAllNotices: " + err);
        dispatch({ type: NOTICE_SET_LOADING, payload: false });
        return dispatch({
            type: NOTICE_SET_ERROR,
            payload: {
                success: false,
                message: "Failed to fetch notices. Please try again.",
                error: err
            }
        });
    }
};

// Set notice filters
export const SetNoticeFilters = (filters: NoticeFilterRequest) => async (dispatch: any) => {
    console.log("Setting notice filters:", filters);
    dispatch({
        type: NOTICE_SET_FILTERS,
        payload: filters
    });
    return dispatch(GetAllNotices(filters));
};

// Clear notice filters
export const ClearNoticeFilters = () => async (dispatch: any) => {
    console.log("Clearing notice filters");
    dispatch({
        type: NOTICE_CLEAR_FILTERS,
        payload: null
    });
    return dispatch(GetAllNotices({ includeInactive: false }));
};

// Set state
export const SetNoticeState = (param: any) => async (dispatch: any) => {
    console.log("Setting notice state:", param);
    return dispatch({
        type: NOTICE_SET_STATE,
        payload: param
    });
};

// Reset notice page
export const ResetNoticePage = () => async (dispatch: any) => {
    console.log("Resetting notice page");
    return dispatch({
        type: NOTICE_PAGE_RESET,
        payload: null
    });
};

// Create new notice - Send EXACT payload from page
export const CreateNotice = (noticeData: any) => async (dispatch: any) => {
    console.log("📝 NOTICE_CREATE action dispatched with payload:", noticeData);
    
    dispatch({ type: NOTICE_CREATE, payload: noticeData });
    dispatch({ type: NOTICE_SET_LOADING, payload: true });
    
    try {
        // Send EXACTLY what came from the page - no modifications
        // The payload already contains action: "I", id: 0, and all other fields
        const payload = {
            action: noticeData.action || "I",     // Use from page or default to "I"
            id: noticeData.id || 0,                // Use from page or default to 0
            category: noticeData.category,
            priority: noticeData.priority,
            year: noticeData.year,
            title: noticeData.title,
            content: noticeData.content,
            author: noticeData.author,
            commentCount: noticeData.commentCount !== undefined ? noticeData.commentCount : 0,
            readCount: noticeData.readCount !== undefined ? noticeData.readCount : 0,
            totalCount: noticeData.totalCount !== undefined ? noticeData.totalCount : 0,
            isActive: noticeData.isActive !== undefined ? noticeData.isActive : true
        };
        
        console.log("Sending EXACT payload to backend:", JSON.stringify(payload, null, 2));
        
        const response = await fetch(
            `${BASE_API_URL}/NoticeCreate`,  // Your backend manage endpoint
            {
                method: "POST",
                headers: postAuthorizedHeaders,
                body: JSON.stringify(payload)
            }
        );
        
        const data: NoticeResponse = await response.json();
        console.log("Create notice response from backend:", data);
        
        dispatch({ type: NOTICE_SET_LOADING, payload: false });
        
        // Check StatusCode from response (201 = Created)
        if (data.StatusCode === 201) {
            console.log("✅ Notice created successfully with ID:", data.Id);
            
            dispatch({
                type: NOTICE_CREATE_SUCCESS,
                payload: data
            });
            
            // Show success message
            dispatch({
                type: NOTICE_SET_STATE,
                payload: {
                    successMessage: data.StatusMessage || "Notice created successfully!"
                }
            });
            
            // Refresh the notices list
            await dispatch(GetAllNotices({ includeInactive: false }));
            
            return { 
                success: true, 
                data: {
                    id: data.Id,
                    message: data.StatusMessage,
                    statusCode: data.StatusCode
                }
            };
        } else {
            const errorMessage = data.StatusMessage || "Failed to create notice";
            console.error("❌ Notice creation failed:", errorMessage);
            
            dispatch({
                type: NOTICE_CREATE_FAILURE,
                payload: errorMessage
            });
            
            dispatch({
                type: NOTICE_SET_ERROR,
                payload: {
                    success: false,
                    message: errorMessage,
                    error: data
                }
            });
            
            return { success: false, error: errorMessage };
        }
    } catch (err) {
        console.error("❌ Error in NoticeAction.ts > CreateNotice: " + err);
        dispatch({ type: NOTICE_SET_LOADING, payload: false });
        
        const errorMessage = "Failed to create notice. Please try again.";
        
        dispatch({
            type: NOTICE_CREATE_FAILURE,
            payload: errorMessage
        });
        
        dispatch({
            type: NOTICE_SET_ERROR,
            payload: {
                success: false,
                message: errorMessage,
                error: err
            }
        });
        
        return { success: false, error: err };
    }
};